import { useState } from "react";
import { Search, ArrowRight, ArrowLeft, HelpCircle, Phone, FileText, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  irsNotices,
  guidedQuestions,
  findNoticeByNumber,
  searchNotices,
  getNoticeById,
  IRSNotice,
} from "@/data/irsNotices";
import NoticeResultCard from "@/components/NoticeResultCard";

interface NoticeLookupToolProps {
  phoneNumber: string;
}

type Mode = "choice" | "search" | "guided" | "result";

interface GuidedState {
  currentQuestionIndex: number;
  answers: Record<string, string>;
}

const NoticeLookupTool = ({ phoneNumber }: NoticeLookupToolProps) => {
  const [mode, setMode] = useState<Mode>("choice");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<IRSNotice[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<IRSNotice | null>(null);
  const [guidedState, setGuidedState] = useState<GuidedState>({
    currentQuestionIndex: 0,
    answers: {},
  });
  const [guidedPath, setGuidedPath] = useState<typeof guidedQuestions[0][]>([guidedQuestions[0]]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      const results = searchNotices(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectNotice = (notice: IRSNotice) => {
    setSelectedNotice(notice);
    setMode("result");
  };

  const handleQuickLookup = () => {
    const notice = findNoticeByNumber(searchQuery);
    if (notice) {
      handleSelectNotice(notice);
    }
  };

  const handleGuidedAnswer = (option: typeof guidedQuestions[0]["options"][0]) => {
    if (option.notices) {
      const notices = option.notices.map((id) => getNoticeById(id)).filter(Boolean) as IRSNotice[];
      if (notices.length === 1) {
        handleSelectNotice(notices[0]);
      } else if (notices.length > 1) {
        setSearchResults(notices);
        setMode("search");
      }
    } else if (option.nextQuestion) {
      const nextQuestion = guidedQuestions.find((q) => q.id === option.nextQuestion);
      if (nextQuestion) {
        setGuidedPath([...guidedPath, nextQuestion]);
        setGuidedState({
          currentQuestionIndex: guidedState.currentQuestionIndex + 1,
          answers: { ...guidedState.answers, [guidedPath[guidedPath.length - 1].id]: option.value },
        });
      }
    }
  };

  const handleGuidedBack = () => {
    if (guidedPath.length > 1) {
      setGuidedPath(guidedPath.slice(0, -1));
      setGuidedState({
        currentQuestionIndex: guidedState.currentQuestionIndex - 1,
        answers: guidedState.answers,
      });
    } else {
      setMode("choice");
    }
  };

  const handleReset = () => {
    setMode("choice");
    setSearchQuery("");
    setSearchResults([]);
    setSelectedNotice(null);
    setGuidedState({ currentQuestionIndex: 0, answers: {} });
    setGuidedPath([guidedQuestions[0]]);
  };

  const handleCall = () => {
    window.location.href = `tel:${phoneNumber.replace(/[^0-9]/g, "")}`;
  };

  const currentQuestion = guidedPath[guidedPath.length - 1];

  return (
    <section className="py-16 md:py-24 bg-navy-dark relative overflow-hidden" id="notice-lookup">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, hsl(var(--gold)) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Tool Header - Always Visible */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Free IRS Notice Lookup Tool</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              What Notice Did You Receive?
            </h2>
            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
              Identify your IRS notice in seconds and understand exactly what to do next.
            </p>
          </div>

          {/* Tool Container */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Mode: Choice */}
            {mode === "choice" && (
              <div className="p-6 md:p-10">
                <div className="grid md:grid-cols-2 gap-6">
                  <button
                    onClick={() => setMode("search")}
                    className="group relative bg-gradient-to-br from-navy-dark to-navy-dark/90 rounded-xl p-8 text-left transition-all hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="absolute top-4 right-4">
                      <ArrowRight className="w-5 h-5 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-gold/20 flex items-center justify-center mb-5">
                      <FileText className="w-7 h-7 text-gold" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mb-2">
                      I Have My Notice
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      Enter your notice number (CP14, LT11, etc.) to see what it means and what to do.
                    </p>
                  </button>

                  <button
                    onClick={() => setMode("guided")}
                    className="group relative bg-gold rounded-xl p-8 text-left transition-all hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="absolute top-4 right-4">
                      <ArrowRight className="w-5 h-5 text-navy-dark opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="w-14 h-14 rounded-xl bg-navy-dark/20 flex items-center justify-center mb-5">
                      <HelpCircle className="w-7 h-7 text-navy-dark" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-navy-dark mb-2">
                      Help Me Identify It
                    </h3>
                    <p className="text-navy-dark/70 text-sm leading-relaxed">
                      Answer a few quick questions and we'll figure out your situation together.
                    </p>
                  </button>
                </div>

              </div>
            )}

            {/* Mode: Search */}
            {mode === "search" && !selectedNotice && (
              <div className="p-6 md:p-10 space-y-6">
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setMode("choice")}
                    className="text-muted-foreground hover:text-navy-dark"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                </div>

                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Enter notice number (e.g., CP504, LT11)"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-12 py-6 text-lg border-2 border-border focus:border-gold"
                    autoFocus
                  />
                  {searchQuery.length >= 2 && (
                    <Button
                      onClick={handleQuickLookup}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-gold hover:bg-gold-dark text-white font-semibold"
                    >
                      Look Up
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground font-medium">
                      {searchResults.length} notice{searchResults.length > 1 ? "s" : ""} found:
                    </p>
                    {searchResults.map((notice) => (
                      <button
                        key={notice.id}
                        onClick={() => handleSelectNotice(notice)}
                        className="w-full bg-muted/50 hover:bg-gold/10 rounded-lg p-4 border border-border hover:border-gold transition-all text-left flex items-center justify-between group"
                      >
                        <div>
                          <span className="font-bold text-navy-dark">
                            {notice.number}
                          </span>
                          <span className="text-muted-foreground mx-2">—</span>
                          <span className="text-muted-foreground">{notice.name}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                      </button>
                    ))}
                  </div>
                )}

                {searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="text-center py-8 bg-muted/30 rounded-xl">
                    <p className="text-muted-foreground mb-4">
                      No exact match found. Try the guided questions instead.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setMode("guided")}
                      className="border-gold text-gold hover:bg-gold hover:text-white"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Answer Guided Questions
                    </Button>
                  </div>
                )}

                {/* Browse All */}
                {searchQuery.length < 2 && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground font-medium">Or browse common notices:</p>
                    <div className="grid gap-2">
                      {irsNotices.slice(0, 6).map((notice) => (
                        <button
                          key={notice.id}
                          onClick={() => handleSelectNotice(notice)}
                          className="w-full bg-muted/50 hover:bg-gold/10 rounded-lg p-4 border border-border hover:border-gold transition-all text-left flex items-center justify-between group"
                        >
                          <div>
                            <span className="font-bold text-navy-dark">
                              {notice.number}
                            </span>
                            <span className="text-muted-foreground mx-2">—</span>
                            <span className="text-muted-foreground">{notice.name}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mode: Guided */}
            {mode === "guided" && (
              <div className="p-6 md:p-10 space-y-6">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    onClick={handleGuidedBack}
                    className="text-muted-foreground hover:text-navy-dark"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <span>Step {guidedPath.length} of 3</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gold transition-all duration-300"
                        style={{
                          width: `${((guidedPath.length) / 3) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-xl p-6 md:p-8">
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-navy-dark mb-6">
                    {currentQuestion.question}
                  </h3>
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleGuidedAnswer(option)}
                        className="w-full p-4 md:p-5 rounded-xl border-2 border-border bg-white hover:border-gold hover:bg-gold/5 transition-all text-left flex items-center justify-between group"
                      >
                        <span className="text-navy-dark font-medium">{option.label}</span>
                        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Guided CTA */}
                <div className="flex items-center justify-center gap-3 text-center pt-4">
                  <p className="text-muted-foreground text-sm">
                    Prefer to talk to someone?
                  </p>
                  <Button
                    onClick={handleCall}
                    variant="link"
                    className="text-gold font-semibold p-0 h-auto"
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call {phoneNumber}
                  </Button>
                </div>
              </div>
            )}

            {/* Mode: Result */}
            {mode === "result" && selectedNotice && (
              <div className="p-6 md:p-10">
                <NoticeResultCard
                  notice={selectedNotice}
                  phoneNumber={phoneNumber}
                  onReset={handleReset}
                />
              </div>
            )}
          </div>

          {/* Bottom CTA Banner */}
          {mode === "choice" && (
            <div className="mt-10 bg-gold rounded-xl p-6 md:p-8 text-center">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-navy-dark mb-2">
                Don't Wait Until It's Too Late
              </h3>
              <p className="text-navy-dark/70 mb-6 max-w-xl mx-auto">
                IRS deadlines are strict. Every day you wait, penalties and interest grow. Get a free consultation now.
              </p>
              <Button
                onClick={handleCall}
                size="lg"
                className="bg-navy-dark hover:bg-navy-dark/90 text-white font-bold text-lg px-8 py-6 shadow-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: {phoneNumber}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NoticeLookupTool;
